import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import Layout from '../components/Layout';
import PageTabBar from '../components/PageTabBar';
import StockSatelliteHeader from '../features/stock/StockSatelliteHeader';
import {
  STOCK_PANEL, STOCK_BTN_PRIMARY, STOCK_BTN_SECONDARY, STOCK_BTN_ICON,
  STOCK_INNER_TAB_ACTIVE, STOCK_INNER_TAB_INACTIVE, STOCK_FILTER_ACTIVE, STOCK_FILTER_INACTIVE, STOCK_KPI_CARD,
} from '../features/stock/stockV2Ui';
import { STOCK_TABS } from './_stockTabs';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import {
  ArrowUpCircle, ArrowDownCircle, ArrowRightLeft, Search, Plus, Package, Barcode, ClipboardList,
  LayoutDashboard, ChevronLeft, ChevronRight, AlertTriangle, CheckCircle, X,
  RefreshCw, TrendingDown, TrendingUp, Archive, Smartphone, History, ExternalLink
} from 'lucide-react';
import { ImeiInputRow } from '../features/stock/StockImeiTraceSection';
import MagasinBadge from '../components/MagasinBadge';

// Backend FR values -> i18n keys (display only, raw fallback). Object keys = backend codes, untranslated.
const MOTIF_LABELS = {
  achat_fournisseur: 'stockMov.motif.achat_fournisseur',
  retour_client: 'stockMov.motif.retour_client',
  correction_inventaire: 'stockMov.motif.correction_inventaire',
  transfert_entrant: 'stockMov.motif.transfert_entrant',
  reception_commande: 'stockMov.motif.reception_commande',
  reintegration_reparation: 'stockMov.motif.reintegration_reparation',
  vente_pos: 'stockMov.motif.vente_pos',
  consommation_reparation: 'stockMov.motif.consommation_reparation',
  perte_casse: 'stockMov.motif.perte_casse',
  retour_fournisseur: 'stockMov.motif.retour_fournisseur',
  transfert_sortant: 'stockMov.motif.transfert_sortant',
  ajustement_inventaire: 'stockMov.motif.ajustement_inventaire',
};

const ETAT_LABELS = { neuf: 'stockMov.etat.neuf', occasion: 'stockMov.etat.occasion', reconditionne: 'stockMov.etat.reconditionne', hs: 'stockMov.etat.hs' };
const STATUT_IMEI_LABELS = { en_stock: 'stockMov.statut.en_stock', vendu: 'stockMov.statut.vendu', reserve: 'stockMov.statut.reserve', en_reparation: 'stockMov.statut.en_reparation' };
const ORIGINE_LABELS = { achat: 'stockMov.origine.achat', reprise_client: 'stockMov.origine.reprise_client', transfert: 'stockMov.origine.transfert' };

const StockMovementsPage = () => {
  const { api, isAdmin } = useAuth();
  const { t } = useLanguage();
  const [searchParams, setSearchParams] = useSearchParams();
  const initialTab = searchParams.get('tab') || 'dashboard';
  const [activeTab, setActiveTab] = useState(initialTab);

  useEffect(() => {
    const tab = searchParams.get('tab');
    if (tab && tab !== activeTab) setActiveTab(tab);
  }, [searchParams, activeTab]);

  const setTab = (tabId) => {
    setActiveTab(tabId);
    const next = new URLSearchParams(searchParams);
    if (tabId === 'dashboard') next.delete('tab');
    else next.set('tab', tabId);
    setSearchParams(next, { replace: true });
  };

  const tabs = [
    { id: 'dashboard', label: t('stockMov.tab.dashboard'), icon: LayoutDashboard },
    { id: 'movements', label: t('stockMov.tab.movements'), icon: ArrowUpCircle },
    { id: 'imei', label: t('stockMov.tab.imei'), icon: Barcode },
    { id: 'inventory', label: t('stockMov.tab.inventory'), icon: ClipboardList },
  ];

  return (
    <Layout>
      <div className="max-w-7xl mx-auto space-y-4 fade-in" data-testid="stock-movements-page">
        <PageTabBar tabs={STOCK_TABS} activeColor="rose" />
        <StockSatelliteHeader
          title={t('stockMov.header.title')}
          subtitle={t('stockMov.header.subtitle')}
          backTo="/stock"
          backTestId="stock-avance-back"
        />

        <div className={`${STOCK_PANEL} p-1 flex gap-1 overflow-x-auto`} data-testid="stock-tabs">
          {tabs.map(tab => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                type="button"
                onClick={() => setTab(tab.id)}
                className={`flex items-center gap-2 px-4 py-2.5 min-h-[44px] rounded-lg text-sm font-semibold whitespace-nowrap transition-all ${
                  activeTab === tab.id ? STOCK_INNER_TAB_ACTIVE : STOCK_INNER_TAB_INACTIVE
                }`}
                data-testid={`stock-tab-${tab.id}`}
              >
                <Icon className="w-4 h-4" />
                {tab.label}
              </button>
            );
          })}
        </div>

        {activeTab === 'dashboard' && <DashboardTab api={api} />}
        {activeTab === 'movements' && <MovementsTab api={api} />}
        {activeTab === 'imei' && (
          <IMEITab
            api={api}
            prefRef={searchParams.get('ref') || ''}
            openAdd={searchParams.get('open') === '1'}
          />
        )}
        {activeTab === 'inventory' && <InventoryTab api={api} isAdmin={isAdmin} />}
      </div>
    </Layout>
  );
};

// ═══════════════════════════════════════════
//  DASHBOARD TAB
// ═══════════════════════════════════════════
const DashboardTab = ({ api }) => {
  const { t, formatCurrency, formatNumber } = useLanguage();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const res = await api.get('/stock-dashboard');
        setData(res.data);
      } catch (err) { console.error(err); }
      finally { setLoading(false); }
    })();
  }, [api]);

  if (loading) return <div className="text-center py-12"><div className="loading-spinner mx-auto"></div></div>;
  if (!data) return <p className="text-gray-400 text-center">{t('stockMov.loadError')}</p>;

  const tot = data.totals || {};
  const fmt = (v) => formatCurrency(v || 0);

  return (
    <div className="space-y-6" data-testid="stock-dashboard-tab">
      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        {[
          { label: t('stockMov.kpi.totalArticles'), testid: 'total-articles', value: tot.total_articles || 0, color: 'text-white' },
          { label: t('stockMov.kpi.qteBoutique'), testid: 'qté-boutique', value: tot.total_quantite_boutique || 0, color: 'text-emerald-400' },
          { label: t('stockMov.kpi.qteDepot'), testid: 'qté-dépôt', value: tot.total_quantite_depot || 0, color: 'text-blue-400' },
          { label: t('stockMov.kpi.ruptures'), testid: 'ruptures', value: data.ruptures_count || 0, color: 'text-red-400' },
          { label: t('stockMov.kpi.imeiEnStock'), testid: 'imei-en-stock', value: data.imei_en_stock || 0, color: 'text-gray-300' },
        ].map(kpi => (
          <div key={kpi.testid} className={STOCK_KPI_CARD} data-testid={`kpi-${kpi.testid}`}>
            <div className="text-xs text-gray-500">{kpi.label}</div>
            <div className={`text-2xl font-bold ${kpi.color}`}>{typeof kpi.value === 'number' ? formatNumber(kpi.value) : kpi.value}</div>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className={`${STOCK_PANEL} p-4`} data-testid="valeur-boutique">
          <h3 className="font-bold text-gray-200 mb-3 flex items-center gap-2 text-sm"><Package className="w-4 h-4 text-emerald-400" /> {t('stockMov.boutique')}</h3>
          <div className="space-y-2 text-sm">
            <div className="flex justify-between"><span className="text-gray-500">{t('stockMov.valeurAchat')}</span><span className="font-bold text-white">{fmt(tot.valeur_achat_boutique)}</span></div>
            <div className="flex justify-between"><span className="text-gray-500">{t('stockMov.valeurVente')}</span><span className="font-bold text-emerald-400">{fmt(tot.valeur_vente_boutique)}</span></div>
            <div className="flex justify-between border-t border-white/10 pt-2"><span className="text-gray-500">{t('stockMov.marge')}</span><span className="font-bold text-gray-300">{fmt((tot.valeur_vente_boutique || 0) - (tot.valeur_achat_boutique || 0))}</span></div>
          </div>
        </div>

        <div className={`${STOCK_PANEL} p-4`} data-testid="valeur-depot">
          <h3 className="font-bold text-gray-200 mb-3 flex items-center gap-2 text-sm"><Archive className="w-4 h-4 text-blue-400" /> {t('stockMov.depot')}</h3>
          <div className="space-y-2 text-sm">
            <div className="flex justify-between"><span className="text-gray-500">{t('stockMov.valeurAchat')}</span><span className="font-bold text-white">{fmt(tot.valeur_achat_depot)}</span></div>
            <div className="flex justify-between"><span className="text-gray-500">{t('stockMov.valeurVente')}</span><span className="font-bold text-emerald-400">{fmt(tot.valeur_vente_depot)}</span></div>
            <div className="flex justify-between border-t border-white/10 pt-2"><span className="text-gray-500">{t('stockMov.marge')}</span><span className="font-bold text-gray-300">{fmt((tot.valeur_vente_depot || 0) - (tot.valeur_achat_depot || 0))}</span></div>
          </div>
        </div>

        <div className={`${STOCK_PANEL} p-4 border-red-500/20`} data-testid="valeur-total">
          <h3 className="font-bold text-gray-200 mb-3 flex items-center gap-2 text-sm"><TrendingUp className="w-4 h-4 text-red-400" /> {t('stockMov.total')}</h3>
          <div className="space-y-2 text-sm">
            <div className="flex justify-between"><span className="text-gray-500">{t('stockMov.valeurAchat')}</span><span className="font-bold text-white">{fmt(tot.valeur_achat)}</span></div>
            <div className="flex justify-between"><span className="text-gray-500">{t('stockMov.valeurVente')}</span><span className="font-bold text-emerald-400">{fmt(tot.valeur_vente)}</span></div>
            <div className="flex justify-between border-t border-white/10 pt-2"><span className="text-gray-500">{t('stockMov.margeTotale')}</span><span className="font-bold text-red-300">{fmt((tot.valeur_vente || 0) - (tot.valeur_achat || 0))}</span></div>
          </div>
        </div>
      </div>

      <div className={`${STOCK_PANEL} p-4`}>
        <h3 className="font-bold text-gray-200 mb-3 flex items-center gap-2"><AlertTriangle className="w-4 h-4 text-red-400" /> {t('stockMov.stocksNegatifs', { count: data.negatifs?.length || 0 })}</h3>
        <div className="max-h-40 overflow-y-auto space-y-1">
          {data.negatifs?.length > 0 ? data.negatifs.map(n => (
            <div key={n.reference} className="flex justify-between text-sm bg-red-500/10 px-3 py-1.5 rounded">
              <span className="truncate">{n.designation}</span>
              <span className="text-red-400 font-bold ml-2">{n.quantite}</span>
            </div>
          )) : <p className="text-sm text-gray-500">{t('stockMov.aucunStockNegatif')}</p>}
        </div>
      </div>

      {/* Articles sous seuil */}
      <div className={`${STOCK_PANEL} p-4`}>
        <h3 className="font-bold text-gray-200 mb-3 flex items-center gap-2 text-sm"><TrendingDown className="w-4 h-4 text-amber-400" /> {t('stockMov.articlesSousSeuil', { count: data.critical_items?.length || 0 })}</h3>
        {data.critical_items?.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="w-full table-dark-custom text-sm">
              <thead><tr><th>{t('stockMov.col.reference')}</th><th>{t('stockMov.col.designation')}</th><th>{t('stockMov.col.stock')}</th><th>{t('stockMov.col.seuil')}</th></tr></thead>
              <tbody>
                {data.critical_items.slice(0, 20).map(item => (
                  <tr key={item.reference}>
                    <td className="font-mono text-xs text-cyan-400">{item.reference}</td>
                    <td>{item.designation}</td>
                    <td className={`font-bold ${item.quantite <= 0 ? 'text-red-400' : 'text-amber-400'}`}>{item.quantite}</td>
                    <td className="text-gray-400">{item.seuil_alerte}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : <p className="text-sm text-gray-500">{t('stockMov.tousAuDessusSeuil')}</p>}
      </div>
    </div>
  );
};

// ═══════════════════════════════════════════
//  MOVEMENTS TAB
// ═══════════════════════════════════════════
const MovementsTab = ({ api }) => {
  const { t, formatDate } = useLanguage();
  const [movements, setMovements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [showAdd, setShowAdd] = useState(false);
  const [motifs, setMotifs] = useState({ entree: [], sortie: [] });
  const [filterType, setFilterType] = useState('');
  // STEP 3.5 multi-magasin (2026-05-15) — magasin filter (all | M1 | M2 | …)
  const [filterMagasin, setFilterMagasin] = useState('');
  const [magasinsList, setMagasinsList] = useState([]);
  const [newMov, setNewMov] = useState({ reference: '', type_mouvement: 'entree', motif: 'achat_fournisseur', quantite: 1, prix_unitaire: 0, fournisseur: '', note: '', imei: '' });
  const [stockSearch, setStockSearch] = useState('');
  const [stockResults, setStockResults] = useState([]);
  const [saving, setSaving] = useState(false);

  const fetchMovements = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page, limit: 30 });
      if (filterType) params.append('type_mouvement', filterType);
      if (filterMagasin) params.append('magasin_id', filterMagasin);
      const res = await api.get(`/stock-movements?${params}`);
      setMovements(res.data.movements || []);
      setTotalPages(res.data.total_pages || 1);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, [api, page, filterType, filterMagasin]);

  useEffect(() => { fetchMovements(); }, [fetchMovements]);
  useEffect(() => {
    api.get('/stock-movements/motifs').then(r => setMotifs(r.data)).catch(() => {});
    api.get('/magasins?active_only=true').then(r => setMagasinsList(r.data?.magasins || [])).catch(() => {});
  }, [api]);

  const searchStock = async (q) => {
    setStockSearch(q);
    if (q.length < 2) { setStockResults([]); return; }
    try {
      const res = await api.get(`/stock?q=${encodeURIComponent(q)}`);
      setStockResults((res.data || []).slice(0, 10));
    } catch (err) { console.error(err); }
  };

  const handleSave = async () => {
    if (!newMov.reference || newMov.quantite <= 0) return alert(t('stockMov.refQtyRequired'));
    setSaving(true);
    try {
      await api.post('/stock-movements', {
        ...newMov,
        quantite: parseInt(newMov.quantite),
        prix_unitaire: parseFloat(newMov.prix_unitaire) || 0,
        imei: newMov.imei || null
      });
      setShowAdd(false);
      setNewMov({ reference: '', type_mouvement: 'entree', motif: 'achat_fournisseur', quantite: 1, prix_unitaire: 0, fournisseur: '', note: '', imei: '' });
      setStockSearch('');
      fetchMovements();
    } catch (err) {
      alert(t('stockMov.error', { detail: err.response?.data?.detail || err.message }));
    } finally { setSaving(false); }
  };

  const currentMotifs = newMov.type_mouvement === 'entree' ? motifs.entree : motifs.sortie;

  return (
    <div className="space-y-4" data-testid="stock-movements-tab">
      <div className="flex flex-wrap gap-2 items-center justify-between">
        <div className="flex gap-2 flex-wrap items-center">
          {['', 'entree', 'sortie'].map(ty => (
            <button key={ty} onClick={() => { setFilterType(ty); setPage(1); }}
              className={`px-3 py-1.5 rounded-lg text-xs ${filterType === ty ? STOCK_FILTER_ACTIVE : STOCK_FILTER_INACTIVE}`}>
              {ty === '' ? t('stockMov.filter.all') : ty === 'entree' ? t('stockMov.filter.entrees') : t('stockMov.filter.sorties')}
            </button>
          ))}
          {/* STEP 3.5 — magasin filter */}
          <div className="flex items-center gap-1 ml-2 border-l border-white/10 pl-2">
            <span className="text-xs text-gray-400 mr-1">{t('stockMov.magasinLabel')}</span>
            <button onClick={() => { setFilterMagasin(''); setPage(1); }}
              data-testid="movements-filter-magasin-all"
              className={`px-2.5 py-1 rounded-lg text-xs ${filterMagasin === '' ? STOCK_FILTER_ACTIVE : STOCK_FILTER_INACTIVE}`}>
              {t('stockMov.filter.all')}
            </button>
            {magasinsList.map(m => (
              <button key={m.code} onClick={() => { setFilterMagasin(m.code); setPage(1); }}
                data-testid={`movements-filter-magasin-${m.code}`}
                className={`px-2.5 py-1 rounded-lg text-xs ${filterMagasin === m.code ? STOCK_FILTER_ACTIVE : STOCK_FILTER_INACTIVE}`}>
                {m.code}
              </button>
            ))}
          </div>
        </div>
        <button type="button" onClick={() => setShowAdd(true)} className={STOCK_BTN_PRIMARY} data-testid="add-movement-btn">
          <Plus className="w-4 h-4" /> {t('stockMov.nouveauMouvement')}
        </button>
      </div>

      <div className={`${STOCK_PANEL} overflow-hidden`}>
        <div className="overflow-x-auto">
          <table className="w-full table-dark-custom text-sm" data-testid="movements-table">
            <thead><tr><th>{t('stockMov.col.date')}</th><th>{t('stockMov.col.type')}</th><th>{t('stockMov.col.motif')}</th><th>{t('stockMov.col.article')}</th><th>{t('stockMov.col.magasin')}</th><th>{t('stockMov.col.qte')}</th><th>{t('stockMov.col.stockAvant')}</th><th>{t('stockMov.col.stockApres')}</th><th>{t('stockMov.col.imei')}</th><th>{t('stockMov.col.par')}</th></tr></thead>
            <tbody>
              {loading ? (
                <tr><td colSpan="10" className="text-center py-8"><div className="loading-spinner mx-auto"></div></td></tr>
              ) : movements.length === 0 ? (
                <tr><td colSpan="10" className="text-center py-8 text-gray-400">{t('stockMov.aucunMouvement')}</td></tr>
              ) : movements.map(m => {
                // STEP 3.5 — render magasin badge(s) depending on movement shape:
                //   transfert → from → to    (chip + arrow + chip)
                //   sale/inv  → single magasin_id chip
                const fromMag = m.from_magasin_id;
                const toMag = m.to_magasin_id;
                const singleMag = m.magasin_id;
                return (
                  <tr key={m.id}>
                    <td className="text-xs text-gray-400">{m.created_at ? formatDate(m.created_at, {day:'2-digit',month:'2-digit',hour:'2-digit',minute:'2-digit'}) : '-'}</td>
                    <td>
                      <span className={`inline-flex items-center gap-1 text-xs font-bold ${m.type_mouvement === 'entree' ? 'text-emerald-400' : m.type_mouvement === 'transfert' ? 'text-blue-400' : 'text-red-400'}`}>
                        {m.type_mouvement === 'transfert' ? <ArrowRightLeft className="w-3 h-3" /> : m.type_mouvement === 'entree' ? <ArrowUpCircle className="w-3 h-3" /> : <ArrowDownCircle className="w-3 h-3" />}
                        {m.type_mouvement === 'transfert' ? t('stockMov.type.transfert') : m.type_mouvement === 'entree' ? t('stockMov.type.entree') : t('stockMov.type.sortie')}
                      </span>
                    </td>
                    <td className="text-xs">{MOTIF_LABELS[m.motif] ? t(MOTIF_LABELS[m.motif]) : m.motif}</td>
                    <td className="font-semibold max-w-[180px] truncate" title={m.designation}>{m.designation || m.reference}</td>
                    <td className="text-xs whitespace-nowrap">
                      {fromMag && toMag ? (
                        <span className="inline-flex items-center gap-1">
                          <span className="px-1.5 py-0.5 rounded bg-amber-500/20 text-amber-300 font-bold">{fromMag}</span>
                          <ArrowRightLeft className="w-3 h-3 text-gray-400" />
                          <span className="px-1.5 py-0.5 rounded bg-emerald-500/20 text-emerald-300 font-bold">{toMag}</span>
                        </span>
                      ) : (
                        <span className="px-1.5 py-0.5 rounded bg-emerald-500/15 text-emerald-300 font-bold">{singleMag || 'M1'}</span>
                      )}
                    </td>
                    <td className="text-center font-bold">{m.quantite}</td>
                    <td className="text-center text-gray-400">{m.ancien_stock != null ? m.ancien_stock : '-'}</td>
                    <td className={`text-center font-bold ${m.nouveau_stock < 0 ? 'text-red-400' : 'text-emerald-400'}`}>{m.nouveau_stock != null ? m.nouveau_stock : '-'}</td>
                    <td className="font-mono text-xs">{m.imei || '-'}</td>
                    <td className="text-xs text-gray-400">{m.admin_username || m.user_id || '-'}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-4 py-2">
          <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1} className="p-2 rounded-lg bg-white/5 hover:bg-white/10 disabled:opacity-50"><ChevronLeft className="w-5 h-5" /></button>
          <span className="text-sm">{t('stockMov.pageOf', { page, total: totalPages })}</span>
          <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages} className="p-2 rounded-lg bg-white/5 hover:bg-white/10 disabled:opacity-50"><ChevronRight className="w-5 h-5" /></button>
        </div>
      )}

      {/* Add movement modal */}
      {showAdd && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" style={{zIndex:9999}}>
          <div className="modal-dark w-full max-w-lg" data-testid="add-movement-modal">
            <div className="modal-dark-header flex items-center justify-between">
              <h3 className="font-bold text-lg">{t('stockMov.modalNewMovement')}</h3>
              <button onClick={() => setShowAdd(false)} className="text-gray-400 hover:text-white"><X className="w-5 h-5" /></button>
            </div>
            <div className="p-5 space-y-4">
              {/* Type */}
              <div className="flex gap-2">
                {['entree', 'sortie'].map(ty => (
                  <button key={ty} onClick={() => setNewMov({ ...newMov, type_mouvement: ty, motif: ty === 'entree' ? 'achat_fournisseur' : 'vente_pos' })}
                    className={`flex-1 py-2.5 rounded-xl text-sm font-bold flex items-center justify-center gap-2 ${
                      newMov.type_mouvement === ty
                        ? (ty === 'entree' ? 'bg-emerald-600 text-white' : 'bg-red-600 text-white')
                        : 'bg-white/10 text-gray-400 hover:bg-white/20'
                    }`}
                    data-testid={`movement-type-${ty}`}>
                    {ty === 'entree' ? <ArrowUpCircle className="w-4 h-4" /> : <ArrowDownCircle className="w-4 h-4" />}
                    {ty === 'entree' ? t('stockMov.type.entree') : t('stockMov.type.sortie')}
                  </button>
                ))}
              </div>

              {/* Motif */}
              <div>
                <label className="block text-sm text-gray-300 mb-1">{t('stockMov.field.motif')}</label>
                <select value={newMov.motif} onChange={e => setNewMov({ ...newMov, motif: e.target.value })} className="input-dark w-full py-2 text-sm" data-testid="movement-motif">
                  {currentMotifs.map(m => <option key={m.value} value={m.value}>{MOTIF_LABELS[m.value] ? t(MOTIF_LABELS[m.value]) : m.label}</option>)}
                </select>
              </div>

              {/* Article search */}
              <div>
                <label className="block text-sm text-gray-300 mb-1">{t('stockMov.field.article')}</label>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <input type="text" value={stockSearch} onChange={e => searchStock(e.target.value)}
                    className="input-dark w-full py-2 pl-9 text-sm" placeholder={t('stockMov.searchArticlePlaceholder')}
                    data-testid="movement-stock-search" />
                </div>
                {stockResults.length > 0 && (
                  <div className="mt-1 max-h-36 overflow-y-auto border border-white/10 rounded-lg">
                    {stockResults.map(s => (
                      <button key={s.reference || s.id} onClick={() => { setNewMov({ ...newMov, reference: s.reference }); setStockSearch(s.designation); setStockResults([]); }}
                        className="w-full text-left px-3 py-2 text-sm hover:bg-white/10">
                        <span className="font-semibold">{s.designation}</span> <span className="text-gray-400 text-xs">{t('stockMov.refStock', { ref: s.reference, qty: s.quantite })}</span>
                      </button>
                    ))}
                  </div>
                )}
                {newMov.reference && <div className="text-xs text-red-300 mt-1">{t('stockMov.refShort', { ref: newMov.reference })}</div>}
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm text-gray-300 mb-1">{t('stockMov.field.quantite')}</label>
                  <input type="number" value={newMov.quantite} onChange={e => setNewMov({ ...newMov, quantite: e.target.value })} className="input-dark w-full py-2 text-sm" min="1" data-testid="movement-quantite" />
                </div>
                <div>
                  <label className="block text-sm text-gray-300 mb-1">{t('stockMov.field.prixUnitaire')}</label>
                  <input type="number" value={newMov.prix_unitaire} onChange={e => setNewMov({ ...newMov, prix_unitaire: e.target.value })} className="input-dark w-full py-2 text-sm" step="0.01" />
                </div>
              </div>

              <div>
                <label className="block text-sm text-gray-300 mb-1">{t('stockMov.field.imeiOptional')}</label>
                <input type="text" value={newMov.imei} onChange={e => setNewMov({ ...newMov, imei: e.target.value })} className="input-dark w-full py-2 text-sm" placeholder={t('stockMov.imeiPlaceholder')} data-testid="movement-imei" />
              </div>

              <div>
                <label className="block text-sm text-gray-300 mb-1">{t('stockMov.field.noteOptional')}</label>
                <input type="text" value={newMov.note} onChange={e => setNewMov({ ...newMov, note: e.target.value })} className="input-dark w-full py-2 text-sm" placeholder={t('stockMov.commentPlaceholder')} />
              </div>

              <div className="flex gap-3 pt-2">
                <button type="button" onClick={() => setShowAdd(false)} className={`${STOCK_BTN_SECONDARY} flex-1 py-2.5`}>{t('stockMov.cancel')}</button>
                <button type="button" onClick={handleSave} disabled={saving || !newMov.reference}
                  className={`${STOCK_BTN_PRIMARY} flex-1 py-2.5`}
                  data-testid="movement-save-btn">
                  {saving ? t('stockMov.saving') : <><CheckCircle className="w-4 h-4" /> {t('stockMov.save')}</>}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ═══════════════════════════════════════════
//  IMEI TAB
// ═══════════════════════════════════════════
const IMEITab = ({ api, prefRef = '', openAdd = false }) => {
  const { t, formatDate } = useLanguage();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [search, setSearch] = useState('');
  const [filterStatut, setFilterStatut] = useState('');
  const [showAdd, setShowAdd] = useState(openAdd);
  const [newImei, setNewImei] = useState({ imei: '', reference: prefRef || '', etat: 'neuf', origine: 'achat', note: '' });
  const [stockSearch, setStockSearch] = useState('');
  const [stockResults, setStockResults] = useState([]);
  const [saving, setSaving] = useState(false);
  const [stats, setStats] = useState(null);
  const [historyImei, setHistoryImei] = useState(null);
  const [historyData, setHistoryData] = useState(null);
  const [historyLoading, setHistoryLoading] = useState(false);

  useEffect(() => {
    if (prefRef) {
      setNewImei((prev) => ({ ...prev, reference: prefRef }));
      setStockSearch(prefRef);
      api.get(`/stock?q=${encodeURIComponent(prefRef)}`).then((res) => {
        const hit = (res.data || []).find((s) => s.reference === prefRef) || (res.data || [])[0];
        if (hit) setStockSearch(hit.designation || prefRef);
      }).catch(() => {});
    }
  }, [prefRef, api]);

  const loadHistory = async (imei) => {
    setHistoryImei(imei);
    setHistoryLoading(true);
    try {
      const res = await api.get(`/stock-imei/history/${encodeURIComponent(imei)}`);
      setHistoryData(res.data);
    } catch {
      setHistoryData(null);
    } finally {
      setHistoryLoading(false);
    }
  };

  const fetchItems = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page, limit: 30 });
      if (search) params.append('search', search);
      if (filterStatut) params.append('statut', filterStatut);
      const res = await api.get(`/stock-imei?${params}`);
      setItems(res.data.items || []);
      setTotalPages(res.data.total_pages || 1);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, [api, page, search, filterStatut]);

  useEffect(() => { fetchItems(); }, [fetchItems]);
  useEffect(() => { api.get('/stock-imei/stats').then(r => setStats(r.data)).catch(() => {}); }, [api]);

  const searchStockForImei = async (q) => {
    setStockSearch(q);
    if (q.length < 2) { setStockResults([]); return; }
    try {
      const res = await api.get(`/stock?q=${encodeURIComponent(q)}`);
      setStockResults((res.data || []).slice(0, 10));
    } catch (err) { console.error(err); }
  };

  const handleSaveImei = async () => {
    if (!newImei.imei || !newImei.reference) return alert(t('stockMov.imeiRefRequired'));
    setSaving(true);
    try {
      await api.post('/stock-imei', {
        ...newImei,
        boutique: (() => {
          try { return localStorage.getItem('pos_current_magasin_id') || 'M1'; } catch { return 'M1'; }
        })(),
      });
      setShowAdd(false);
      setNewImei({ imei: '', reference: '', etat: 'neuf', origine: 'achat', note: '' });
      setStockSearch('');
      fetchItems();
    } catch (err) {
      alert(t('stockMov.error', { detail: err.response?.data?.detail || err.message }));
    } finally { setSaving(false); }
  };

  return (
    <div className="space-y-4" data-testid="stock-imei-tab">
      <div className={`${STOCK_PANEL} p-4 flex flex-wrap gap-3 items-start justify-between`}>
        <div className="flex items-start gap-3 max-w-2xl">
          <Smartphone className="w-5 h-5 text-red-400 shrink-0 mt-0.5" />
          <div>
            <h3 className="text-sm font-bold text-white">{t('stockMov.registreTitle')}</h3>
            <p className="text-xs text-gray-500 mt-1">
              {t('stockMov.registreDesc')}
            </p>
          </div>
        </div>
        <a href="/stock/ajouter" className={`${STOCK_BTN_SECONDARY} text-xs`} data-testid="imei-add-phone-link">
          <ExternalLink className="w-3.5 h-3.5" /> {t('stockMov.nouveauTelephone')}
        </a>
      </div>

      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {Object.entries(STATUT_IMEI_LABELS).map(([k, v]) => (
            <div key={k} className={STOCK_KPI_CARD}>
              <div className="text-xs text-gray-500">{t(v)}</div>
              <div className="text-xl font-bold text-white">{stats.par_statut?.[k] || 0}</div>
            </div>
          ))}
        </div>
      )}

      <div className="flex flex-wrap gap-2 items-center justify-between">
        <div className="flex gap-2 items-center">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input type="text" value={search} onChange={e => setSearch(e.target.value)} onKeyDown={e => e.key === 'Enter' && fetchItems()}
              className="input-dark py-2 pl-9 text-sm w-64" placeholder={t('stockMov.searchImeiPlaceholder')} data-testid="imei-search" />
          </div>
          <select value={filterStatut} onChange={e => { setFilterStatut(e.target.value); setPage(1); }} className="input-dark py-2 text-sm">
            <option value="">{t('stockMov.tousLesStatuts')}</option>
            {Object.entries(STATUT_IMEI_LABELS).map(([k, v]) => <option key={k} value={k}>{t(v)}</option>)}
          </select>
        </div>
        <button type="button" onClick={() => setShowAdd(true)} className={STOCK_BTN_PRIMARY} data-testid="add-imei-btn">
          <Plus className="w-4 h-4" /> {t('stockMov.enregistrerImei')}
        </button>
      </div>

      <div className={`${STOCK_PANEL} overflow-hidden`}>
        <div className="overflow-x-auto">
          <table className="w-full table-dark-custom text-sm" data-testid="imei-table">
            <thead><tr><th>{t('stockMov.col.imeiSerie')}</th><th>{t('stockMov.col.refDot')}</th><th>{t('stockMov.col.article')}</th><th>{t('stockMov.col.magShort')}</th><th>{t('stockMov.col.etat')}</th><th>{t('stockMov.col.statut')}</th><th>{t('stockMov.col.origine')}</th><th>{t('stockMov.col.date')}</th><th></th></tr></thead>
            <tbody>
              {loading ? (
                <tr><td colSpan="9" className="text-center py-8"><div className="loading-spinner mx-auto"></div></td></tr>
              ) : items.length === 0 ? (
                <tr><td colSpan="9" className="text-center py-10 text-gray-500">
                  <Smartphone className="w-8 h-8 mx-auto mb-2 opacity-40" />
                  <p className="text-sm">{t('stockMov.aucunImei')}</p>
                </td></tr>
              ) : items.map(item => (
                <tr key={item.id} className="hover:bg-white/[0.02]">
                  <td className="font-mono text-gray-200 text-xs">{item.imei}</td>
                  <td className="font-mono text-xs text-gray-500">{item.reference}</td>
                  <td className="max-w-[180px] truncate text-white">{item.designation}</td>
                  <td className="text-xs font-bold text-gray-400">{item.boutique || 'M1'}</td>
                  <td><span className="px-2 py-0.5 rounded text-xs bg-white/5">{ETAT_LABELS[item.etat] ? t(ETAT_LABELS[item.etat]) : item.etat}</span></td>
                  <td><span className={`px-2 py-0.5 rounded text-xs font-semibold ${
                    item.statut === 'en_stock' ? 'bg-emerald-500/20 text-emerald-400' :
                    item.statut === 'vendu' ? 'bg-blue-500/20 text-blue-400' :
                    item.statut === 'reserve' ? 'bg-amber-500/20 text-amber-400' :
                    'bg-red-500/20 text-red-400'
                  }`}>{STATUT_IMEI_LABELS[item.statut] ? t(STATUT_IMEI_LABELS[item.statut]) : item.statut}</span></td>
                  <td className="text-xs text-gray-400">{ORIGINE_LABELS[item.origine] ? t(ORIGINE_LABELS[item.origine]) : item.origine}</td>
                  <td className="text-xs text-gray-400">{item.created_at ? formatDate(item.created_at) : '-'}</td>
                  <td className="text-right">
                    <button type="button" onClick={() => loadHistory(item.imei)} className="p-1.5 rounded-lg border border-white/10 text-gray-400 hover:text-white" title={t('stockMov.historique')}>
                      <History className="w-3.5 h-3.5" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-4 py-2">
          <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1} className="p-2 rounded-lg bg-white/5 hover:bg-white/10 disabled:opacity-50"><ChevronLeft className="w-5 h-5" /></button>
          <span className="text-sm">{t('stockMov.pageOf', { page, total: totalPages })}</span>
          <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages} className="p-2 rounded-lg bg-white/5 hover:bg-white/10 disabled:opacity-50"><ChevronRight className="w-5 h-5" /></button>
        </div>
      )}

      {historyImei && (
        <div className={`${STOCK_PANEL} p-4`} data-testid="imei-history-panel">
          <div className="flex items-center justify-between mb-3">
            <h4 className="text-sm font-bold text-white font-mono">{t('stockMov.historiqueImei', { imei: historyImei })}</h4>
            <button type="button" onClick={() => { setHistoryImei(null); setHistoryData(null); }} className="text-gray-500 hover:text-white"><X className="w-4 h-4" /></button>
          </div>
          {historyLoading ? (
            <div className="text-center py-4 text-gray-500 text-sm">{t('stockMov.loading')}</div>
          ) : historyData?.movements?.length ? (
            <ul className="space-y-2 text-xs">
              {historyData.movements.map((m, i) => (
                <li key={i} className="flex flex-wrap gap-2 py-2 border-b border-white/5 text-gray-400">
                  <span>{m.created_at ? formatDate(m.created_at, { dateStyle: 'short', timeStyle: 'short' }) : '—'}</span>
                  <span className="text-white">{m.type_mouvement === 'entree' ? t('stockMov.type.entree') : m.type_mouvement === 'sortie' ? t('stockMov.type.sortie') : m.type_mouvement === 'transfert' ? t('stockMov.type.transfert') : m.type_mouvement}</span>
                  <span>{MOTIF_LABELS[m.motif] ? t(MOTIF_LABELS[m.motif]) : m.motif}</span>
                  <span className="text-gray-500">{t('stockMov.byUser', { user: m.admin_username || '—' })}</span>
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-xs text-gray-500">{t('stockMov.aucunMouvementImei')}</p>
          )}
        </div>
      )}

      {/* Add IMEI modal */}
      {showAdd && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" style={{zIndex:9999}}>
          <div className="modal-dark w-full max-w-md" data-testid="add-imei-modal">
            <div className="modal-dark-header flex items-center justify-between">
              <h3 className="font-bold text-lg">{t('stockMov.modalRegisterImei')}</h3>
              <button onClick={() => setShowAdd(false)} className="text-gray-400 hover:text-white"><X className="w-5 h-5" /></button>
            </div>
            <div className="p-5 space-y-4">
              <ImeiInputRow
                imei={newImei.imei}
                onImeiChange={(v) => setNewImei({ ...newImei, imei: v })}
                reference={newImei.reference}
              />
              <div>
                <label className="block text-sm text-gray-300 mb-1">{t('stockMov.field.articleStockRef')}</label>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <input type="text" value={stockSearch} onChange={e => searchStockForImei(e.target.value)} className="input-dark w-full py-2 pl-9 text-sm" placeholder={t('stockMov.searchPlaceholder')} />
                </div>
                {stockResults.length > 0 && (
                  <div className="mt-1 max-h-32 overflow-y-auto border border-white/10 rounded-lg">
                    {stockResults.map(s => (
                      <button key={s.reference || s.id} onClick={() => { setNewImei({ ...newImei, reference: s.reference }); setStockSearch(s.designation); setStockResults([]); }}
                        className="w-full text-left px-3 py-2 text-sm hover:bg-white/10">
                        <span className="font-semibold">{s.designation}</span> <span className="text-xs text-gray-400">({s.reference})</span>
                      </button>
                    ))}
                  </div>
                )}
                {newImei.reference && <div className="text-xs text-red-300 mt-1">{t('stockMov.refShort', { ref: newImei.reference })}</div>}
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm text-gray-300 mb-1">{t('stockMov.field.etat')}</label>
                  <select value={newImei.etat} onChange={e => setNewImei({ ...newImei, etat: e.target.value })} className="input-dark w-full py-2 text-sm">
                    {Object.entries(ETAT_LABELS).map(([k, v]) => <option key={k} value={k}>{t(v)}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-sm text-gray-300 mb-1">{t('stockMov.field.origine')}</label>
                  <select value={newImei.origine} onChange={e => setNewImei({ ...newImei, origine: e.target.value })} className="input-dark w-full py-2 text-sm">
                    <option value="achat">{t('stockMov.origine.achat')}</option>
                    <option value="reprise_client">{t('stockMov.origine.reprise_client')}</option>
                    <option value="transfert">{t('stockMov.origine.transfert')}</option>
                  </select>
                </div>
              </div>
              <div className="flex gap-3 pt-2">
                <button type="button" onClick={() => setShowAdd(false)} className={`${STOCK_BTN_SECONDARY} flex-1 py-2.5`}>{t('stockMov.cancel')}</button>
                <button type="button" onClick={handleSaveImei} disabled={saving || !newImei.imei || !newImei.reference}
                  className={`${STOCK_BTN_PRIMARY} flex-1 py-2.5 disabled:opacity-50`} data-testid="imei-save-btn">
                  {saving ? t('stockMov.saving') : t('stockMov.save')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ═══════════════════════════════════════════
//  INVENTORY TAB
// ═══════════════════════════════════════════
const InventoryTab = ({ api, isAdmin }) => {
  const { t, formatDate } = useLanguage();
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedSession, setSelectedSession] = useState(null);
  const [sessionItems, setSessionItems] = useState([]);
  const [loadingSession, setLoadingSession] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const [newTitle, setNewTitle] = useState('');
  const [creating, setCreating] = useState(false);
  const [countRef, setCountRef] = useState('');
  const [countQty, setCountQty] = useState('');
  const [counting, setCounting] = useState(false);
  const [stockSearchInv, setStockSearchInv] = useState('');
  const [stockResultsInv, setStockResultsInv] = useState([]);
  const [countArticle, setCountArticle] = useState(null);

  const fetchSessions = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/inventory/sessions');
      setSessions(res.data.sessions || []);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, [api]);

  useEffect(() => { fetchSessions(); }, [fetchSessions]);

  const loadSession = async (id) => {
    setLoadingSession(true);
    try {
      const res = await api.get(`/inventory/sessions/${id}`);
      setSelectedSession(res.data.session);
      setSessionItems(res.data.items || []);
    } catch (err) { console.error(err); }
    finally { setLoadingSession(false); }
  };

  const handleCreate = async () => {
    setCreating(true);
    try {
      await api.post('/inventory/sessions', { title: newTitle || undefined });
      setShowCreate(false);
      setNewTitle('');
      fetchSessions();
    } catch (err) { alert(t('stockMov.error', { detail: err.response?.data?.detail || err.message })); }
    finally { setCreating(false); }
  };

  const searchStockInv = async (q) => {
    setStockSearchInv(q);
    if (q.length < 2) {
      setStockResultsInv([]);
      setCountArticle(null);
      return;
    }
    try {
      const res = await api.get(`/stock/search/${encodeURIComponent(q)}`, {
        params: { include_all_magasins: true },
      });
      setStockResultsInv((res.data || []).slice(0, 12));
    } catch (err) { console.error(err); }
  };

  const selectCountArticle = (article) => {
    setCountRef(article.reference);
    setStockSearchInv(article.designation || article.reference);
    setCountArticle(article);
    setStockResultsInv([]);
  };

  const handleCount = async () => {
    if (!countRef || !selectedSession) return;
    setCounting(true);
    try {
      const res = await api.post(`/inventory/sessions/${selectedSession.id}/count`, {
        reference: countRef, comptage_physique: parseInt(countQty) || 0
      });
      const ecartStr = `${res.data.ecart > 0 ? '+' : ''}${res.data.ecart}`;
      alert(t('stockMov.countSaved', { theorique: res.data.stock_theorique, physique: res.data.comptage_physique, ecart: ecartStr }));
      setCountRef(''); setCountQty(''); setStockSearchInv(''); setCountArticle(null);
      loadSession(selectedSession.id);
    } catch (err) { alert(t('stockMov.error', { detail: err.response?.data?.detail || err.message })); }
    finally { setCounting(false); }
  };

  const handleValidate = async (applyCorrections) => {
    if (!selectedSession) return;
    if (!window.confirm(applyCorrections ? t('stockMov.confirmValidateApply') : t('stockMov.confirmValidateNoApply'))) return;
    try {
      await api.post(`/inventory/sessions/${selectedSession.id}/validate`, { apply_corrections: applyCorrections });
      alert(t('stockMov.sessionValidee'));
      loadSession(selectedSession.id);
      fetchSessions();
    } catch (err) { alert(t('stockMov.error', { detail: err.response?.data?.detail || err.message })); }
  };

  return (
    <div className="space-y-4" data-testid="stock-inventory-tab">
      {!selectedSession ? (
        <>
          <div className="flex justify-between items-center">
            <h3 className="font-bold text-gray-200">{t('stockMov.sessionsInventaire')}</h3>
            {isAdmin && (
              <button type="button" onClick={() => setShowCreate(true)} className={STOCK_BTN_PRIMARY} data-testid="create-inventory-btn">
                <Plus className="w-4 h-4" /> {t('stockMov.nouvelInventaire')}
              </button>
            )}
          </div>

          {loading ? <div className="text-center py-8"><div className="loading-spinner mx-auto"></div></div> : (
            <div className="space-y-2">
              {sessions.length === 0 ? <p className="text-gray-400 text-center py-8">{t('stockMov.aucuneSession')}</p> : sessions.map(s => (
                <div key={s.id} onClick={() => loadSession(s.id)} className={`${STOCK_PANEL} p-4 cursor-pointer hover:border-white/20 transition-colors`} data-testid={`inventory-session-${s.id}`}>
                  <div className="flex justify-between items-center">
                    <div>
                      <div className="font-semibold">{s.title}</div>
                      <div className="text-xs text-gray-400">{s.created_at ? formatDate(s.created_at, { dateStyle: 'short', timeStyle: 'short' }) : ''} — {t('stockMov.parUser', { user: s.created_by })}</div>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="text-xs text-gray-400">{t('stockMov.articlesEcarts', { articles: s.total_articles, ecarts: s.total_ecarts })}</span>
                      <span className={`px-2 py-1 rounded text-xs font-bold ${
                        s.status === 'en_cours' ? 'bg-amber-500/20 text-amber-400' :
                        s.status === 'valide' ? 'bg-emerald-500/20 text-emerald-400' :
                        'bg-gray-500/20 text-gray-400'
                      }`}>{s.status === 'en_cours' ? t('stockMov.status.enCours') : s.status === 'valide' ? t('stockMov.status.valide') : s.status}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      ) : (
        <>
          <button onClick={() => setSelectedSession(null)} className="text-sm text-gray-400 hover:text-white flex items-center gap-1"><ChevronLeft className="w-4 h-4" /> {t('stockMov.retourSessions')}</button>
          <div className="flex justify-between items-center">
            <div>
              <h3 className="font-bold text-gray-200 text-lg">{selectedSession.title}</h3>
              <div className="text-xs text-gray-400">
                {t('stockMov.statutColon')} <span className={selectedSession.status === 'valide' ? 'text-emerald-400' : 'text-amber-400'}>{selectedSession.status === 'en_cours' ? t('stockMov.status.enCours') : t('stockMov.status.valide')}</span>
                {selectedSession.validated_by && <> — {t('stockMov.validePar', { user: selectedSession.validated_by })}</>}
              </div>
            </div>
            {isAdmin && selectedSession.status === 'en_cours' && (
              <div className="flex gap-2">
                <button type="button" onClick={() => handleValidate(false)} className={STOCK_BTN_SECONDARY}>{t('stockMov.validerSansCorrection')}</button>
                <button type="button" onClick={() => handleValidate(true)} className={STOCK_BTN_PRIMARY} data-testid="validate-inventory-btn">{t('stockMov.validerCorriger')}</button>
              </div>
            )}
          </div>

          {/* Counting form */}
          {selectedSession.status === 'en_cours' && (
            <div className={`${STOCK_PANEL} p-4`}>
              <h4 className="font-semibold text-gray-200 mb-3 text-sm">{t('stockMov.comptagePhysique')}</h4>
              <div className="flex flex-wrap gap-3 items-end">
                <div className="flex-1 min-w-[200px]">
                  <label className="block text-xs text-gray-400 mb-1">{t('stockMov.field.article')}</label>
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <input type="text" value={stockSearchInv} onChange={e => searchStockInv(e.target.value)}
                      className="input-dark w-full py-2 pl-9 text-sm" placeholder={t('stockMov.searchArticleShort')} />
                  </div>
                  {stockResultsInv.length > 0 && (
                    <div className="mt-1 max-h-48 overflow-y-auto border border-white/10 rounded-lg" data-testid="inventory-search-dropdown">
                      {stockResultsInv.map((s) => (
                        <button
                          key={`${s.id || s.reference}-${s.magasin_id || 'M1'}`}
                          type="button"
                          onClick={() => selectCountArticle(s)}
                          className="w-full text-left px-3 py-2 text-sm hover:bg-white/10 border-b border-white/5 last:border-0"
                          data-testid={`inventory-search-result-${s.reference}`}
                        >
                          <div className="flex items-center justify-between gap-2">
                            <span className="truncate text-white">{s.designation}</span>
                            <MagasinBadge code={s.magasin_id || 'M1'} size="sm" />
                          </div>
                          <div className="flex flex-wrap gap-2 text-[10px] text-gray-500 mt-0.5">
                            <span className="font-mono">{s.reference}</span>
                            {s.ean && <span>EAN {s.ean}</span>}
                            <span>{t('stockMov.inv.bdShort', { b: s.quantite || 0, d: s.quantite_depot || 0 })}</span>
                          </div>
                        </button>
                      ))}
                    </div>
                  )}
                  {countArticle && (
                    <div className="mt-2 flex flex-wrap items-center gap-2 text-xs text-gray-400" data-testid="inventory-selected-article">
                      <span>{t('stockMov.stockSelectionne')}</span>
                      <MagasinBadge code={countArticle.magasin_id || 'M1'} size="md" showName />
                      <span className="font-mono text-gray-500">{countArticle.reference}</span>
                      <span className="text-gray-500">
                        {t('stockMov.dotBoutique', { q: countArticle.quantite || 0 })}
                        {(countArticle.quantite_depot || 0) > 0 && ` ${t('stockMov.dotDepot', { q: countArticle.quantite_depot })}`}
                      </span>
                    </div>
                  )}
                </div>
                <div className="w-28">
                  <label className="block text-xs text-gray-400 mb-1">{t('stockMov.qteComptee')}</label>
                  <input type="number" value={countQty} onChange={e => setCountQty(e.target.value)} className="input-dark w-full py-2 text-sm" min="0" data-testid="inventory-count-qty" />
                </div>
                <button type="button" onClick={handleCount} disabled={counting || !countRef} className={`${STOCK_BTN_PRIMARY} disabled:opacity-50`} data-testid="inventory-count-btn">
                  {counting ? '...' : t('stockMov.save')}
                </button>
              </div>
            </div>
          )}

          {/* Inventory items */}
          {loadingSession ? <div className="text-center py-8"><div className="loading-spinner mx-auto"></div></div> : (
            <div className={`${STOCK_PANEL} overflow-hidden`}>
              <div className="overflow-x-auto">
                <table className="w-full table-dark-custom text-sm" data-testid="inventory-items-table">
                  <thead><tr><th>{t('stockMov.col.reference')}</th><th>{t('stockMov.col.designation')}</th><th>{t('stockMov.col.stockTheorique')}</th><th>{t('stockMov.col.comptagePhysique')}</th><th>{t('stockMov.col.ecart')}</th><th>{t('stockMov.col.comptePar')}</th></tr></thead>
                  <tbody>
                    {sessionItems.length === 0 ? (
                      <tr><td colSpan="6" className="text-center py-8 text-gray-400">{t('stockMov.aucunArticleCompte')}</td></tr>
                    ) : sessionItems.map(item => (
                      <tr key={item.reference}>
                        <td className="font-mono text-xs text-gray-400">{item.reference}</td>
                        <td>{item.designation}</td>
                        <td className="text-center">{item.stock_theorique}</td>
                        <td className="text-center font-bold">{item.comptage_physique}</td>
                        <td className={`text-center font-bold ${item.ecart > 0 ? 'text-emerald-400' : item.ecart < 0 ? 'text-red-400' : 'text-gray-400'}`}>
                          {item.ecart > 0 ? '+' : ''}{item.ecart}
                        </td>
                        <td className="text-xs text-gray-400">{item.counted_by}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </>
      )}

      {/* Create session modal */}
      {showCreate && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" style={{zIndex:9999}}>
          <div className="modal-dark w-full max-w-md">
            <div className="modal-dark-header flex items-center justify-between">
              <h3 className="font-bold text-lg">{t('stockMov.modalNewSession')}</h3>
              <button onClick={() => setShowCreate(false)} className="text-gray-400 hover:text-white"><X className="w-5 h-5" /></button>
            </div>
            <div className="p-5 space-y-4">
              <div>
                <label className="block text-sm text-gray-300 mb-1">{t('stockMov.field.titreOptional')}</label>
                <input type="text" value={newTitle} onChange={e => setNewTitle(e.target.value)} className="input-dark w-full py-2 text-sm" placeholder={t('stockMov.titrePlaceholder')} />
              </div>
              <div className="flex gap-3 pt-2">
                <button type="button" onClick={() => setShowCreate(false)} className={`${STOCK_BTN_SECONDARY} flex-1 py-2.5`}>{t('stockMov.cancel')}</button>
                <button type="button" onClick={handleCreate} disabled={creating} className={`${STOCK_BTN_PRIMARY} flex-1 py-2.5 disabled:opacity-50`} data-testid="create-inventory-confirm">
                  {creating ? t('stockMov.creating') : t('stockMov.creer')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default StockMovementsPage;
